import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import { getCourseModuleContent } from '../../../services/adminService';
import Loader from '../../../components/admin/Loader';
import AdminPage from '../../../components/admin/AdminPage';
import { toast } from 'react-toastify';
import NoData from "../../../components/common/NoData";

function AdminModuleContentView() {
    const { courseId, moduleId } = useParams();
    const decodedCourseId = decodeData(courseId);
    const decodedModuleId = decodeData(moduleId);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [contents, setContents] = useState([]);
    const [classroomName, setClassroomName] = useState(null);

    useEffect(() => {
        fetchModuleContent();
    }, []);

    const fetchModuleContent = async () => {
        try {
            setLoading(true);
            const response = await getCourseModuleContent({ module_id: decodedModuleId });
            setClassroomName(response.course_name);
            
            if (response.success) {
                const formattedContents = response.data.response.map(item => ({
                    id: item.id,
                    type: item.type,
                    title: item.title,
                    description: item.type === 'video' ? (item.description || 'No description') : '',
                    url: item.url || item.attachment_detail,
                    duration: item.duration || 'N/A',
                    date: new Date(item.createdAt).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric'
                    }),
                    status: item.is_active ? 'active' : 'inactive',
                    totalQuestions: item.total_questions || 0,
                    timeLimit: item.duration ? `${item.duration} minutes` : 'No time limit',
                    passingPercentage: item.pass_percentage || 0,
                    earnPoint: item.earn_point || 0,
                    fileType: item.url ? item.url.split('.').pop().toUpperCase() : 'FILE',
                }));
                
                formattedContents.sort((a, b) => a.serial_no - b.serial_no);
                setContents(formattedContents);
            } else {
                setContents([]);
            }
        } catch (error) {
            setContents([]);
            toast.error('Error fetching module content: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleContentClick = (content) => {
        const encodedContentId = encodeData(content.id);
        const baseUrl = `/admin/approvalRequest/view/${courseId}/content/${moduleId}`;
        
        switch (content.type) {
            case 'video':
                navigate(`${baseUrl}/video/${encodedContentId}`);
                break;
            case 'document':
                navigate(`${baseUrl}/document/${encodedContentId}`);
                break;
            case 'assessment':
                navigate(`${baseUrl}/quiz/${encodedContentId}`);
                break;
            case 'survey':
                navigate(`${baseUrl}/survey/${encodedContentId}`);
                break;
        }
    };

    const renderContentMeta = (content) => {
        const baseContainer = "text-gray-500 text-xs flex flex-wrap items-center gap-x-3 gap-y-1";
        const baseItem = "inline-flex items-center gap-1";

        switch (content.type) {
            case 'video':
                return (
                    <div className={baseContainer}>
                        <div className={baseItem}>
                            <Icon icon="fluent:clock-24-regular" width={14} height={14} />
                            <span>{content.duration}</span>
                        </div>
                        <div className={baseItem}>
                            <Icon icon="fluent:calendar-24-regular" width={14} height={14} />
                            <span>{content.date}</span>
                        </div>
                    </div>
                );
            case 'assessment':
                return (
                    <div className={baseContainer}>
                        <div className={baseItem}>
                            <Icon icon="fluent:question-circle-24-regular" width={14} height={14} />
                            <span>{content.totalQuestions} Questions</span>
                        </div>
                        <div className={baseItem}>
                            <Icon icon="fluent:timer-24-regular" width={14} height={14} />
                            <span>{content.timeLimit}</span>
                        </div>
                        <div className={baseItem}>
                            <Icon icon="fluent:trophy-24-regular" width={14} height={14} />
                            <span>{content.passingPercentage}% Pass</span>
                        </div>
                    </div>
                );
            case 'survey':
                return (
                    <div className={baseContainer}>
                        <div className={baseItem}>
                            <Icon icon="fluent:timer-24-regular" width={14} height={14} />
                            <span>{content.timeLimit}</span>
                        </div>
                        <div className={baseItem}>
                            <Icon icon="fluent:calendar-24-regular" width={14} height={14} />
                            <span>{content.date}</span>
                        </div>
                    </div>
                );
            case 'document':
                return (
                    <div className={baseContainer}>
                        <div className={baseItem}>
                            <Icon icon="fluent:document-24-regular" width={14} height={14} />
                            <span>{content.fileType}</span>
                        </div>
                        <div className={baseItem}>
                            <Icon icon="fluent:calendar-24-regular" width={14} height={14} />
                            <span>{content.date}</span>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <AdminPage
            title="Module Content (View Only)"
            description={classroomName ? `Viewing content for: ${classroomName}` : 'Module content details'}
            actions={
                <Link
                    to={`/admin/approvalRequest/view/${courseId}`}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 no-underline"
                >
                    <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
                    Back to Course
                </Link>
            }
        >
            {loading ? (
                <div className="flex justify-center items-center min-h-[320px] rounded-2xl bg-gray-50/50 border border-gray-100">
                    <Loader />
                </div>
            ) : contents.length === 0 ? (
                <NoData message="No content found for this module" />
            ) : (
                <div className="space-y-2.5">
                    {contents.map((content, index) => (
                        <div 
                            key={`${content.type}-${content.id}`} 
                            className="w-full"
                            onClick={() => handleContentClick(content)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:border-[#4F6BFF] hover:shadow-md transition-all">
                                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                                    <div className="text-[10px] font-medium text-gray-400">
                                        #{index + 1}
                                    </div>
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${
                                        content.status === 'active'
                                            ? 'bg-green-50 text-green-700 border-green-200'
                                            : 'bg-gray-50 text-gray-600 border-gray-200'
                                    }`}>
                                        {content.status === 'active' ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                <div className="p-3 flex gap-3 items-start">
                                    <div className="flex-shrink-0">
                                        {content.type === 'document' ? (
                                            <div className="flex items-center justify-center w-[100px] h-[75px] rounded-lg bg-gray-50 border border-dashed border-gray-200">
                                                <Icon icon="fluent:document-pdf-24-regular" width={32} height={32} className="text-primary" />
                                            </div>
                                        ) : content.type === 'video' ? (
                                            <video
                                                src={content.url}
                                                className="w-[120px] h-[75px] rounded-lg object-cover bg-black/5 border border-gray-200"
                                                controls={false}
                                                preload="metadata"
                                            />
                                        ) : (
                                            <div className="flex items-center justify-center w-[100px] h-[75px] rounded-lg bg-gray-50 border border-gray-200">
                                                <Icon
                                                    icon={content.type === 'assessment' ? "fluent:quiz-new-24-regular" : "fluent:form-24-regular"}
                                                    width={28}
                                                    height={28}
                                                    className="text-gray-500"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-medium">
                                                {content.type === 'video' && <><Icon icon="fluent:video-24-regular" width={12} height={12} /><span>Video</span></>}
                                                {content.type === 'survey' && <><Icon icon="fluent:form-24-regular" width={12} height={12} /><span>Survey</span></>}
                                                {content.type === 'assessment' && <><Icon icon="fluent:quiz-new-24-regular" width={12} height={12} /><span>Assessment</span></>}
                                                {content.type === 'document' && <><Icon icon="fluent:document-24-regular" width={12} height={12} /><span>Document</span></>}
                                            </span>
                                        </div>
                                        <h6 className="mb-1 text-sm font-semibold text-gray-900 truncate">
                                            {content.title}
                                        </h6>
                                        <div className="mt-1">
                                            {renderContentMeta(content)}
                                        </div>
                                        {content.type === 'video' && content.description && (
                                            <p className="text-gray-500 text-xs mt-1.5 line-clamp-2">
                                                {content.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AdminPage>
    );
}

export default AdminModuleContentView;
